import { IBrowserAccessor } from './browser';
import { ILogger } from './logger';
import { expect } from './expect';
import { NDaysStorageStrategy } from './storage-strategies/n-days-storage-strategy';
import { IStorageStrategy } from './storage-strategies/storage-strategy.interface';
import { DefaultStorageStrategy } from './storage-strategies/default-storage-strategy';

const STORAGE_KEY_UTM_PARAMETERS = 'GlobalE_Analytics_UTMs';

const UTM_SOURCE = 'utm_source';
const UTM_MEDIUM = 'utm_medium';
const UTM_CAMPAIGN = 'utm_campaign';
const UTM_TERM = 'utm_term';
const UTM_CONTENT = 'utm_content';
const UTM_ADSET = 'utm_adset';

export type UtmHandlerConfig = {
  FT_3DA: boolean;
  FT_3DA_UTM_SOURCE_LIST?: string[];
  FT_3DA_STORAGE_LIFETIME?: number;
};

/** When it's not possible to read URL by the logic of the UtmHandler
 * then URL can be provided outside as a part of UtmPreset. */
export type Url = string;

/** When it's not possible to read UTMs from URL or Storages then
 * this override can be used to provide UTMs directly. */
export type UtmParams = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  utmAdSet: string;
};

export type UtmPreset = Url | UtmParams;

export class UtmHandler {
  private readonly browser: IBrowserAccessor;
  private readonly logger: ILogger;
  private readonly FT_3DA: boolean;
  private readonly FT_3DA_UTM_SOURCE_LIST: string[];
  private readonly FT_3DA_STORAGE_LIFETIME: number;

  public utmSource: string;
  public utmMedium: string;
  public utmCampaign: string;
  public utmTerm: string;
  public utmContent: string;
  public utmAdSet: string;

  /**
   * Initializes a new instance of a class that manages UTM parameters.
   */
  constructor(browser: IBrowserAccessor, logger: ILogger, config?: UtmHandlerConfig) {
    this.browser = expect(browser).toBeDefined('The IBrowserAccessor implementation is required to construct UtmHandler.');
    this.logger = expect(logger).toBeDefined('The ILogger implementation is required to construct UtmHandler.');

    this.FT_3DA = config?.FT_3DA ?? true;
    this.FT_3DA_UTM_SOURCE_LIST = config?.FT_3DA_UTM_SOURCE_LIST ?? ['borderfree'];
    this.FT_3DA_STORAGE_LIFETIME = config?.FT_3DA_STORAGE_LIFETIME ?? 4320;
  }

  /**
   * Ensures UTM parameters are acquired from the storage, otherwise parses URL to find them
   * and saves them in both old and new ways. If no UTM parameters found then default value will be set.
   */
  public async handle(preset?: UtmPreset): Promise<void> {
    const utmParams = this.getUtmParams(preset);
    const utmStorageStrategy = this.selectStorageStrategy(utmParams);
    const output = await utmStorageStrategy.execute(utmParams);
    this.utmSource = output.utmSource;
    this.utmMedium = output.utmMedium;
    this.utmCampaign = output.utmCampaign;
    this.utmTerm = output.utmTerm;
    this.utmContent = output.utmContent;
    this.utmAdSet = output.utmAdSet;
  }

  private getUtmParams(input?: string | UtmPreset): UtmParams {
    const DEFAULT_UTM_SOURCE = 'direct';
    const DEFAULT_UTM_MEDIUM = '(none)';
    const DEFAULT_UTM_CAMPAIGN = '(not-set)';

    try {
      if (isUrl(input)) {
        // if URL with UTMs provided outside, then parse it assuming it contains UTMs
        return getFromPresetUrl(input);
      }
      if (isUtmParams(input)) {
        // if object with UTMs provided then use it as output
        return getFromPresetObject(input);
      }

      // default flow is to parse URL or get defaults based on referrer
      const url = new URL(this.browser.getPageUrl());
      return {
        utmSource: url.searchParams.get(UTM_SOURCE) ?? getDefaultUtmSource(this.browser),
        utmMedium: url.searchParams.get(UTM_MEDIUM) ?? getDefaultUtmMedium(this.browser),
        utmCampaign: url.searchParams.get(UTM_CAMPAIGN) ?? getDefaultUtmCampaign(),
        utmTerm: url.searchParams.get(UTM_TERM),
        utmContent: url.searchParams.get(UTM_CONTENT),
        utmAdSet: url.searchParams.get(UTM_ADSET),
      };
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'UtmHandler::getUtmParams()',
        message: error.message,
        errorMessage: error.message,
        errorStackTrace: error.stackTrace,
      });
      // default fallback model
      return {
        utmSource: DEFAULT_UTM_SOURCE,
        utmMedium: DEFAULT_UTM_MEDIUM,
        utmCampaign: DEFAULT_UTM_CAMPAIGN,
        utmTerm: null,
        utmContent: null,
        utmAdSet: null,
      };
    }

    function isUrl(_input: unknown): _input is string {
      return typeof _input === 'string';
    }
    function isUtmParams(_input: unknown): _input is UtmParams {
      return (
        typeof _input === 'object' &&
        _input !== null &&
        'utmSource' in _input &&
        'utmMedium' in _input &&
        'utmCampaign' in _input &&
        typeof (_input as UtmParams).utmSource === 'string' &&
        typeof (_input as UtmParams).utmMedium === 'string' &&
        typeof (_input as UtmParams).utmCampaign === 'string'
      );
    }
    function getFromPresetUrl(_input: string): UtmParams {
      const url = new URLSearchParams(_input);
      return {
        utmSource: url.get(UTM_SOURCE),
        utmMedium: url.get(UTM_MEDIUM),
        utmCampaign: url.get(UTM_CAMPAIGN),
        utmTerm: url.get(UTM_TERM),
        utmContent: url.get(UTM_CONTENT),
        utmAdSet: url.get(UTM_ADSET),
      };
    }
    function getFromPresetObject(_input: UtmParams): UtmParams {
      return _input;
    }
    function getDefaultUtmSource(_browser: IBrowserAccessor) {
      const referrer = _browser.getReferrer();
      if (referrer) {
        if (referrer.includes('google')) {
          return 'google';
        } else if (referrer.includes('borderfree')) {
          return 'borderfree';
        } else {
          return referrer;
        }
      }

      return DEFAULT_UTM_SOURCE;
    }
    function getDefaultUtmMedium(_browser: IBrowserAccessor) {
      const referrer = _browser.getReferrer();
      if (referrer) {
        if (referrer.includes('google')) {
          return 'organic';
        } else {
          return 'referral';
        }
      }

      return DEFAULT_UTM_MEDIUM;
    }
    function getDefaultUtmCampaign() {
      return DEFAULT_UTM_CAMPAIGN;
    }
  }

  private selectStorageStrategy(utmParams: UtmParams): IStorageStrategy<UtmParams> {
    // check if Borderfree (aka "3 Days Attribution") feature is enabled and meet requirements
    // otherwise use default strategy

    if (this.FT_3DA) {
      return new NDaysStorageStrategy<UtmParams>(this.browser, this.FT_3DA_STORAGE_LIFETIME, STORAGE_KEY_UTM_PARAMETERS, () =>
        this.FT_3DA_UTM_SOURCE_LIST.includes(utmParams.utmSource),
      );
    }
    // add another strategies here...

    return new DefaultStorageStrategy(this.browser, STORAGE_KEY_UTM_PARAMETERS);
  }
}
